import { useContext, useEffect, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { cloneDeep } from 'es-toolkit';

import {
  Accordion,
  Anchor,
  Box,
  Checkbox,
  FormField,
  FormHelp,
  Label,
  LabeledControl,
  Select,
  Text,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import { useRentalManagerUserPropertySummaryWebApiHandler_applications } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { useModularListingProgress } from '../../hooks/queries/modularListingProgress';
import { useConvertSingleFamilyToRoomForRent } from '../../hooks/queries/roomForRent';
import {
  syncOnDataChangePropertyDetailsOverview,
  useGetPropertyDetailsOverview,
} from '../../hooks/queries/useGetPropertyDetailsOverview';
import { RMWContext } from '../../providers/RMWContext';
import { RoutingSelectors } from '../../selectors';
import { useSelector } from '../../selectors/useSelector';
import propertyUtils from '../../utils/propertyUtils';

import NotificationConstants from '../../constants/NotificationConstants';
import PropertyConstants from '../../constants/PropertyConstants';
import { STEP } from '../../containers/CreateListingPage/constants';

import RoomForRentCheckbox from '../../../../src/components/modals/AddPropertyModal/components/RoomForRentCheckbox';

import type { ChangeEvent } from 'react';

const {
  PROPERTY_TYPES: { CONDO, HOUSE, TOWNHOUSE },
} = PropertyConstants;

export type CorrectPropertyTypes = typeof CONDO | typeof HOUSE | typeof TOWNHOUSE;

type Props = {
  correctPropertyType: CorrectPropertyTypes;
  onSelectChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  onConvertComplete?: () => void;
};

const EditPropertyType = ({ correctPropertyType, onSelectChange, onConvertComplete = () => {} }: Props) => {
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { paths } = useContext(RMWContext);

  const listingAlias = useSelector((state) => RoutingSelectors.makeListingId(state));
  const {
    properties: { currentProperty },
  } = useSelector(
    (state) => ({
      app: state.app,
      properties: state.properties,
    }),
    shallowEqual,
  );

  const [acknowledged, setAcknowledged] = useState(false);

  const { data: propertyDetailsOverviewData } = useGetPropertyDetailsOverview(listingAlias);

  const { mutate: convertSingleFamilyToRoomForRent } = useConvertSingleFamilyToRoomForRent();

  const { mutate: modularListingProgressMutate } = useModularListingProgress();

  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });

  const [accordionData, setAccordianData] = useState([
    {
      title: 'Need to list an entire apartment community?',
      content:
        'If this property always has availability, you can list it as an apartment community. If you’d prefer to list units individually, create a separate property for each unit.',
      linkText: 'Options for advertising multifamily properties',
      linkHref: 'https://www.zillow.com/z/rental-property-advertising/',
    },
    {
      title: 'Published listing shows wrong property type?',
      content:
        'If you’ve set the correct property type in Zillow Rental Manager, but the published listing still shows the wrong type, the type you selected may have been overwritten by public records data.',
    },
    {
      condition: !isRoomForRent,
      title: 'Need to list this property as a Room for Rent?',
      content:
        'Room for Rent listings don’t have access to Applications, Leases, and Payments or our Premium offerings and won’t display on Trulia.',
      acknowledgement: 'I understand this action can’t be undone.',
      linkText: 'Convert this property to a Room for Rent',
      action: () => {
        convertSingleFamilyToRoomForRent(listingAlias, {
          onSuccess: ({
            isConverted,
            listingAlias: convertedListingAlias,
          }: {
            isConverted: boolean;
            listingAlias: string;
          }) => {
            queryClient.invalidateQueries({ queryKey: ['getPropertyDetailsOverview', listingAlias] });
            queryClient.invalidateQueries({
              queryKey: useRentalManagerUserPropertySummaryWebApiHandler_applications.getKey({ listingAlias }),
            });

            enqueueToast(<Toast tone={NotificationConstants.SUCCESS}>Successfully converted to Room for Rent</Toast>);

            if (isConverted) {
              modularListingProgressMutate({
                propertyId: convertedListingAlias,
                step: STEP.ROOM_WHO_IS_LISTING_PROPERTY.toLowerCase(),
              });

              if (convertedListingAlias !== listingAlias) {
                return window.router.transitionTo(
                  `${paths.propertiesPage}/${convertedListingAlias}/dashboard/overview`,
                );
              }
              // if `rerouted` no need to reset cache to update stepper
              queryClient.invalidateQueries({ queryKey: ['getListingSummary', listingAlias] });

              return onConvertComplete();
            }
          },
          onError: (error) => {
            const {
              error: { message },
            } = error as unknown as { error: { message: string } };

            const accordionCopy = cloneDeep(accordionData);
            accordionCopy[2].error = `There was an error with converting your property. Message: ${message}`;

            setAccordianData(accordionCopy);
          },
        });
      },
      error: '',
    },
  ]);

  useEffect(() => {
    if (propertyDetailsOverviewData) {
      syncOnDataChangePropertyDetailsOverview({ data: propertyDetailsOverviewData, currentProperty, dispatch });
    }
  }, [propertyDetailsOverviewData]);

  return (
    <div>
      <FormField css={{ marginBlock: '300' }}>
        <Label>Property type</Label>
        <Select
          name="propertyType"
          aria-label="Property type"
          value={correctPropertyType}
          onChange={onSelectChange}
          css={{ whiteSpace: 'pre' }}
        >
          <option value={HOUSE}>{HOUSE}</option>
          <option value={CONDO}>{CONDO}</option>
          <option value={TOWNHOUSE}>{TOWNHOUSE}</option>
        </Select>
      </FormField>

      {isRoomForRent && <RoomForRentCheckbox isRoomForRentChecked={isRoomForRent} isRoomForRentDisabled={true} />}

      {accordionData.map((dataPoint, index) => {
        if (dataPoint.condition !== undefined ? dataPoint.condition : true) {
          return (
            <Box key={`accordionData-${dataPoint.title}`} css={{ marginBottom: '200' }}>
              <Accordion.Root>
                <Accordion.Item value={`edit-property-type-${index}`}>
                  <Accordion.Header>{dataPoint.title}</Accordion.Header>
                  <Accordion.Panel>
                    <Text asChild>
                      <p>
                        {dataPoint.content}
                        {dataPoint.acknowledgement && (
                          <LabeledControl
                            label={<Label>{dataPoint.acknowledgement}</Label>}
                            control={
                              <Checkbox checked={acknowledged} onChange={() => setAcknowledged(!acknowledged)} />
                            }
                            css={{ marginBlock: '200' }}
                          />
                        )}
                        {dataPoint.linkText && dataPoint.linkHref && (
                          <Anchor
                            href={dataPoint.linkHref}
                            target="_blank"
                            css={{ display: 'inline-block', marginBlock: '400' }}
                          >
                            {dataPoint.linkText}
                          </Anchor>
                        )}
                      </p>
                    </Text>
                    {dataPoint.linkText && dataPoint.action && (
                      <TextButton
                        disabled={Boolean(dataPoint.acknowledgement && !acknowledged)}
                        textStyle="body-sm"
                        css={{ marginBlock: '200' }}
                        onClick={dataPoint.action}
                      >
                        {dataPoint.linkText}
                      </TextButton>
                    )}
                    {dataPoint.error && <FormHelp error={true}>{dataPoint.error}</FormHelp>}
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion.Root>
            </Box>
          );
        }
        return null;
      })}
    </div>
  );
};

export default EditPropertyType;
